import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Sparkles, AlertTriangle, Loader2, Brain, Search, Eye,
  ChevronDown, ChevronUp, AlertOctagon, Copy, Users, FileWarning,
  TrendingUp, Ban, CheckCircle2, RefreshCw, Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAccounting } from "@/contexts/AccountingContext";
import { useExpenses } from "@/contexts/ExpenseContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

interface FraudFinding {
  id: string;
  category: string;
  severity: string;
  title: string;
  description: string;
  evidence: string;
  affectedAmount?: number;
  affectedParty?: string;
  recommendation: string;
  riskScore: number;
  status?: "open" | "investigating" | "resolved" | "dismissed";
}

interface FraudReport {
  overallRiskScore: number;
  overallRiskLevel: string;
  summary: string;
  findings: FraudFinding[];
  statistics: {
    totalTransactionsAnalyzed: number;
    flaggedTransactions: number;
    totalAmountAtRisk: number;
    criticalFindings: number;
    highFindings: number;
    mediumFindings: number;
    lowFindings: number;
  };
}

const CATEGORY_META: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string; color: string }> = {
  duplicate_payment: { icon: Copy, label: "Duplicate Payment", color: "destructive" },
  abnormal_expense: { icon: TrendingUp, label: "Abnormal Expense", color: "warning" },
  suspicious_vendor: { icon: Users, label: "Suspicious Vendor", color: "destructive" },
  invoice_manipulation: { icon: FileWarning, label: "Invoice Manipulation", color: "destructive" },
  revenue_leakage: { icon: Ban, label: "Revenue Leakage", color: "warning" },
  ghost_employee: { icon: Users, label: "Ghost Employee", color: "destructive" },
  other: { icon: AlertTriangle, label: "Other", color: "accent" },
};

function RiskGauge({ score, level }: { score: number; level: string }) {
  const color = score >= 75 ? "destructive" : score >= 50 ? "warning" : score >= 25 ? "accent" : "success";
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className="stroke-muted" />
          <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" strokeDasharray={`${score * 2.64} 264`} strokeLinecap="round" className={`stroke-${color} transition-all duration-1000`} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-xl font-bold text-${color}`}>{score}</span>
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Risk</span>
        </div>
      </div>
      <span className={`text-[10px] font-semibold uppercase tracking-wider mt-1 px-2 py-0.5 rounded-full bg-${color}/10 text-${color} border border-${color}/20`}>
        {level}
      </span>
    </div>
  );
}

function SeverityIcon({ severity }: { severity: string }) {
  const config: Record<string, { color: string; icon: React.ComponentType<{ className?: string }> }> = {
    critical: { color: "text-destructive", icon: AlertOctagon },
    high: { color: "text-destructive", icon: AlertTriangle },
    medium: { color: "text-warning", icon: AlertTriangle },
    low: { color: "text-accent", icon: Eye },
  };
  const { color, icon: Icon } = config[severity] || config.low;
  return <Icon className={`h-4 w-4 ${color}`} />;
}

function FindingCard({ finding, onUpdateStatus }: { finding: FraudFinding; onUpdateStatus: (id: string, status: FraudFinding["status"]) => void }) {
  const [expanded, setExpanded] = useState(false);
  const meta = CATEGORY_META[finding.category] || CATEGORY_META.other;
  const CategoryIcon = meta.icon;
  const status = finding.status || "open";

  const severityStyles: Record<string, string> = {
    critical: "border-destructive/30 bg-destructive/5",
    high: "border-destructive/20 bg-destructive/3",
    medium: "border-warning/20 bg-warning/3",
    low: "border-border",
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`rounded-xl border bg-card overflow-hidden ${severityStyles[finding.severity] || ""}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-start gap-3 text-left hover:bg-muted/10 transition-colors">
        <SeverityIcon severity={finding.severity} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{finding.title}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-${meta.color}/10 text-${meta.color} border-${meta.color}/20`}>
              {meta.label}
            </span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
              status === "resolved" ? "bg-success/10 text-success" :
              status === "investigating" ? "bg-warning/10 text-warning" :
              status === "dismissed" ? "bg-muted text-muted-foreground" :
              "bg-destructive/10 text-destructive"
            }`}>
              {status}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{finding.description}</p>
          <div className="flex items-center gap-4 mt-2">
            {finding.affectedAmount && (
              <span className="text-xs font-bold text-foreground">{fmt(finding.affectedAmount)}</span>
            )}
            {finding.affectedParty && (
              <span className="text-xs text-muted-foreground">{finding.affectedParty}</span>
            )}
            <span className="text-[10px] text-muted-foreground">Risk: {finding.riskScore}/100</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 space-y-3">
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Evidence</span>
                <p className="text-xs text-foreground mt-1">{finding.evidence}</p>
              </div>

              <div className="p-3 rounded-lg bg-accent/5 border border-accent/15">
                <span className="text-[10px] uppercase tracking-wider text-accent font-medium">Recommended Action</span>
                <p className="text-xs text-foreground mt-1">{finding.recommendation}</p>
              </div>

              {status === "open" && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => onUpdateStatus(finding.id, "investigating")} className="h-7 text-xs gap-1">
                    <Search className="h-3 w-3" /> Investigate
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onUpdateStatus(finding.id, "resolved")} className="h-7 text-xs gap-1 text-success border-success/30 hover:bg-success/10">
                    <CheckCircle2 className="h-3 w-3" /> Resolve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onUpdateStatus(finding.id, "dismissed")} className="h-7 text-xs gap-1 text-muted-foreground">
                    Dismiss
                  </Button>
                </div>
              )}
              {status === "investigating" && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => onUpdateStatus(finding.id, "resolved")} className="h-7 text-xs gap-1 text-success border-success/30 hover:bg-success/10">
                    <CheckCircle2 className="h-3 w-3" /> Mark Resolved
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const AIFraudDetectionPage = () => {
  const { vouchers, ledgers } = useAccounting();
  const { expenses } = useExpenses();
  const { activeCompany } = useCompany();
  const { addNotification } = useNotifications();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<FraudReport | null>(null);
  const [aiPowered, setAiPowered] = useState(false);

  const transactionData = useMemo(() => {
    const approved = vouchers.filter((v) => v.status === "Approved" || v.status === "Pending");
    return {
      vouchers: approved.map((v) => ({
        id: v.id, number: v.voucherNumber, type: v.type, date: v.date,
        amount: v.totalDebit, party: v.partyName, narration: v.narration,
        status: v.status, invoiceNumber: v.invoiceNumber,
        gst: v.gstAmount, cgst: v.cgst, sgst: v.sgst, igst: v.igst,
      })),
      ledgerSummary: ledgers.slice(0, 30).map((l) => ({
        name: l.name, group: l.group, balance: l.currentBalance,
        balanceType: l.currentBalanceType, gstin: l.gstin,
      })),
      expenses: expenses.slice(0, 30).map((e) => ({
        vendor: e.vendor, amount: e.amount, category: e.category,
        date: e.date, status: e.status, description: e.description,
      })),
      totalVouchers: approved.length,
      totalExpenses: expenses.length,
    };
  }, [vouchers, ledgers, expenses]);

  const alertAdmins = useCallback((findings: FraudFinding[]) => {
    const critical = findings.filter((f) => f.severity === "critical" || f.severity === "high");
    if (critical.length > 0) {
      addNotification({
        type: "system",
        title: "⚠️ Fraud Alert: Suspicious Activity Detected",
        message: `AI detected ${critical.length} high-severity issue${critical.length > 1 ? "s" : ""}: ${critical.map((f) => f.title).join(", ")}`,
        priority: "critical",
        channels: ["dashboard", "email"],
        actionUrl: "/dashboard/ai-fraud-detection",
        actionLabel: "Review Findings",
      });
    }
  }, [addNotification]);

  const runScan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/ai/fraud-detection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: activeCompany?.id || null }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "AI service is currently unavailable. Please try again later.");
      }

      const r = json.data as FraudReport;
      r.findings = r.findings.map((f: FraudFinding) => ({ ...f, status: "open" as const }));
      setReport(r);
      setAiPowered(true);
      alertAdmins(r.findings);
      toast({ title: "AI Fraud Scan Complete", description: `${r.findings.length} findings detected.` });
    } catch (err: any) {
      console.error("AI fraud scan failed:", err);
      toast({
        title: "AI Service Notice",
        description: err.message || "AI Fraud Detection is currently unavailable. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [activeCompany, toast, alertAdmins]);

  const updateFindingStatus = useCallback((id: string, status: FraudFinding["status"]) => {
    if (!report) return;
    setReport({
      ...report,
      findings: report.findings.map((f) => f.id === id ? { ...f, status } : f),
    });
    toast({ title: `Finding ${status}`, description: status === "resolved" ? "Marked as resolved." : status === "investigating" ? "Under investigation." : "Dismissed." });
  }, [report, toast]);

  const openFindings = report?.findings.filter((f) => f.status === "open" || f.status === "investigating") || [];
  const resolvedFindings = report?.findings.filter((f) => f.status === "resolved" || f.status === "dismissed") || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Shield className="h-6 w-6 text-destructive" />
            AI Fraud Detection
          </h1>
          <p className="text-sm text-muted-foreground">Real-time AI-powered anomaly detection & transaction monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          {aiPowered && (
            <span className="text-[10px] uppercase tracking-wider text-accent font-medium bg-accent/10 px-2.5 py-1 rounded-full flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> AI Powered
            </span>
          )}
          <Button onClick={runScan} disabled={loading} variant={report ? "outline" : "default"} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
            {loading ? "Scanning..." : report ? "Re-scan" : "Run AI Fraud Scan"}
          </Button>
        </div>
      </div>

      {/* Report */}
      {report ? (
        <div className="space-y-4">
          {/* Summary banner */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`p-5 rounded-xl border ${
              report.overallRiskLevel === "critical" ? "bg-destructive/5 border-destructive/20" :
              report.overallRiskLevel === "high" ? "bg-destructive/3 border-destructive/15" :
              report.overallRiskLevel === "medium" ? "bg-warning/5 border-warning/20" :
              "bg-success/5 border-success/20"
            }`}
          >
            <div className="flex items-start gap-6">
              <RiskGauge score={report.overallRiskScore} level={report.overallRiskLevel} />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground mb-1">Fraud Risk Assessment</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{report.summary}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <div className="text-center p-2 rounded-lg bg-card/50 border border-border/50">
                    <p className="text-lg font-bold text-foreground">{report.statistics.totalTransactionsAnalyzed}</p>
                    <p className="text-[10px] text-muted-foreground">Analyzed</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-card/50 border border-border/50">
                    <p className="text-lg font-bold text-destructive">{report.statistics.flaggedTransactions}</p>
                    <p className="text-[10px] text-muted-foreground">Flagged</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-card/50 border border-border/50">
                    <p className="text-lg font-bold text-warning">{fmt(report.statistics.totalAmountAtRisk)}</p>
                    <p className="text-[10px] text-muted-foreground">At Risk</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-card/50 border border-border/50">
                    <p className="text-lg font-bold text-foreground">
                      <span className="text-destructive">{report.statistics.criticalFindings}</span>
                      <span className="text-muted-foreground text-xs mx-0.5">/</span>
                      <span className="text-warning">{report.statistics.highFindings}</span>
                      <span className="text-muted-foreground text-xs mx-0.5">/</span>
                      <span className="text-accent">{report.statistics.mediumFindings}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground">C / H / M</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Findings tabs */}
          <Tabs defaultValue="active">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="active" className="gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Active ({openFindings.length})
              </TabsTrigger>
              <TabsTrigger value="resolved" className="gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" /> Resolved ({resolvedFindings.length})
              </TabsTrigger>
              <TabsTrigger value="all" className="gap-1.5">
                <Eye className="h-3.5 w-3.5" /> All ({report.findings.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-3 mt-4">
              {openFindings.length === 0 ? (
                <div className="text-center py-8"><CheckCircle2 className="h-8 w-8 text-success mx-auto mb-2" /><p className="text-sm text-muted-foreground">All findings resolved!</p></div>
              ) : (
                openFindings.sort((a, b) => b.riskScore - a.riskScore).map((f) => (
                  <FindingCard key={f.id} finding={f} onUpdateStatus={updateFindingStatus} />
                ))
              )}
            </TabsContent>

            <TabsContent value="resolved" className="space-y-3 mt-4">
              {resolvedFindings.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">No resolved findings yet.</div>
              ) : (
                resolvedFindings.map((f) => (
                  <FindingCard key={f.id} finding={f} onUpdateStatus={updateFindingStatus} />
                ))
              )}
            </TabsContent>

            <TabsContent value="all" className="space-y-3 mt-4">
              {report.findings.sort((a, b) => b.riskScore - a.riskScore).map((f) => (
                <FindingCard key={f.id} finding={f} onUpdateStatus={updateFindingStatus} />
              ))}
            </TabsContent>
          </Tabs>
        </div>
      ) : !loading ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">AI-Powered Fraud Detection</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
            AccouPro AI analyzes all transactions for duplicate payments, abnormal expenses, suspicious vendor activity, and other fraud indicators.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-md mx-auto">
            {[
              { icon: Copy, label: "Duplicate Payments" },
              { icon: TrendingUp, label: "Abnormal Expenses" },
              { icon: Users, label: "Vendor Analysis" },
              { icon: FileWarning, label: "Invoice Manipulation" },
              { icon: Ban, label: "Revenue Leakage" },
              { icon: Bell, label: "Real-time Alerts" },
            ].map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-muted/30 border border-border/50">
                <f.icon className="h-4 w-4 text-destructive/70" />
                <span className="text-[10px] font-medium text-muted-foreground">{f.label}</span>
              </div>
            ))}
          </div>
          <Button onClick={runScan} className="mt-6 gap-2">
            <Brain className="h-4 w-4" /> Run AI Fraud Scan
          </Button>
        </div>
      ) : (
        <div className="text-center py-16">
          <Loader2 className="h-10 w-10 text-destructive animate-spin mx-auto mb-4" />
          <h3 className="text-sm font-semibold text-foreground">Scanning transactions for anomalies...</h3>
          <p className="text-xs text-muted-foreground mt-1">AI is analyzing vouchers, expenses & vendor patterns</p>
        </div>
      )}
    </div>
  );
};

// Fallback local fraud detection
function generateLocalFraudReport(data: Record<string, any>): FraudReport {
  const findings: FraudFinding[] = [];
  const vouchers = data.vouchers || [];
  let totalAtRisk = 0;

  // Detect duplicate payments (same party + similar amount)
  const paymentMap = new Map<string, any[]>();
  vouchers.filter((v: any) => v.type === "Payment" || v.type === "Purchase").forEach((v: any) => {
    const key = `${v.party || "unknown"}-${Math.round(v.amount / 100) * 100}`;
    if (!paymentMap.has(key)) paymentMap.set(key, []);
    paymentMap.get(key)!.push(v);
  });
  paymentMap.forEach((group, key) => {
    if (group.length > 1) {
      const amount = group[0].amount;
      totalAtRisk += amount;
      findings.push({
        id: `dup-${key}`, category: "duplicate_payment", severity: "high",
        title: `Potential Duplicate Payment to ${group[0].party || "Unknown"}`,
        description: `${group.length} similar transactions of ~${fmt(amount)} found for the same party.`,
        evidence: `Vouchers: ${group.map((v: any) => v.number).join(", ")}. Amounts: ${group.map((v: any) => fmt(v.amount)).join(", ")}.`,
        affectedAmount: amount * group.length, affectedParty: group[0].party,
        recommendation: "Compare invoice numbers and dates. If duplicate, initiate recovery.",
        riskScore: 72, status: "open",
      });
    }
  });

  // Detect abnormal expenses (>3x average)
  const amounts = vouchers.map((v: any) => v.amount).filter((a: number) => a > 0);
  const avg = amounts.length > 0 ? amounts.reduce((s: number, a: number) => s + a, 0) / amounts.length : 0;
  vouchers.forEach((v: any) => {
    if (v.amount > avg * 3 && avg > 0) {
      totalAtRisk += v.amount;
      findings.push({
        id: `abn-${v.id}`, category: "abnormal_expense", severity: "medium",
        title: `Abnormally Large Transaction: ${v.number}`,
        description: `${fmt(v.amount)} is ${(v.amount / avg).toFixed(1)}x the average transaction of ${fmt(avg)}.`,
        evidence: `Voucher ${v.number} (${v.type}) dated ${v.date}. Party: ${v.party || "N/A"}. Narration: "${v.narration || "N/A"}".`,
        affectedAmount: v.amount, affectedParty: v.party,
        recommendation: "Verify transaction with supporting documentation. Check approval trail.",
        riskScore: 55, status: "open",
      });
    }
  });

  // Detect round-number suspicious amounts
  vouchers.forEach((v: any) => {
    if (v.amount >= 50000 && v.amount % 10000 === 0) {
      findings.push({
        id: `rnd-${v.id}`, category: "other", severity: "low",
        title: `Round-Number Transaction: ${fmt(v.amount)}`,
        description: `Perfectly round amounts can indicate estimated or fabricated entries.`,
        evidence: `Voucher ${v.number}, amount ${fmt(v.amount)}, party: ${v.party || "N/A"}.`,
        affectedAmount: v.amount, affectedParty: v.party,
        recommendation: "Request original invoice/receipt to validate the exact amount.",
        riskScore: 30, status: "open",
      });
    }
  });

  // Vendor without GSTIN
  const noGstin = (data.ledgerSummary || []).filter((l: any) => l.group === "Sundry Creditors" && !l.gstin && l.balance > 0);
  if (noGstin.length > 0) {
    findings.push({
      id: "no-gstin", category: "suspicious_vendor", severity: "medium",
      title: `${noGstin.length} Vendor(s) Without GSTIN`,
      description: `Vendors without GSTIN may be unregistered or fictitious entities.`,
      evidence: `Vendors: ${noGstin.map((l: any) => l.name).join(", ")}`,
      affectedAmount: noGstin.reduce((s: number, l: any) => s + l.balance, 0),
      recommendation: "Verify vendor registration. Unregistered vendors above ₹5,000 attract reverse charge under GST.",
      riskScore: 48, status: "open",
    });
  }

  const critical = findings.filter((f) => f.severity === "critical").length;
  const high = findings.filter((f) => f.severity === "high").length;
  const medium = findings.filter((f) => f.severity === "medium").length;
  const low = findings.filter((f) => f.severity === "low").length;
  const riskScore = Math.min(100, critical * 25 + high * 15 + medium * 8 + low * 3);

  return {
    overallRiskScore: riskScore,
    overallRiskLevel: riskScore >= 75 ? "critical" : riskScore >= 50 ? "high" : riskScore >= 25 ? "medium" : "low",
    summary: `Analyzed ${vouchers.length} transactions and ${(data.expenses || []).length} expense records. Found ${findings.length} potential issues with ${fmt(totalAtRisk)} at risk. ${critical + high > 0 ? "Immediate review recommended for high-severity findings." : "No critical issues detected."}`,
    findings,
    statistics: {
      totalTransactionsAnalyzed: vouchers.length + (data.expenses || []).length,
      flaggedTransactions: findings.length,
      totalAmountAtRisk: totalAtRisk,
      criticalFindings: critical,
      highFindings: high,
      mediumFindings: medium,
      lowFindings: low,
    },
  };
}

export default AIFraudDetectionPage;
